<template>
  <div 
    :class="containerClass"
    :style="containerStyles"
  >
    <div class="limit-header" :class="{ 'compact': isCompactMode }">
      <h3 class="limit-title" :style="{ color: titleColor, fontFamily: titleFontFamily }">{{ title }}</h3>
    </div>
    <div class="limit-body" :class="{ 'compact-mode': isCompactMode }">
      <div class="chart-wrapper" :style="chartWrapperStyles">
        <apexchart
          type="donut"
          :options="chartOptions"
          :series="series"
          :height="chartHeight"
        />
        <div class="center-label" :class="{ 'compact': isCompactMode }">
          <span class="current-value">{{ formattedAvailable }}</span>
          <span class="total-value">/ {{ formattedTotal }}</span>
        </div>
      </div>
      <div class="legend" :class="{ 'compact': isCompactMode }">
        <div
          v-for="item in legendItems"
          :key="item.key"
          class="legend-item"
        >
          <span class="legend-color" :style="{ backgroundColor: item.color }" />
          <div class="legend-info">
            <span class="legend-label">{{ item.label }}</span>
            <span class="legend-amount">{{ item.formattedValue }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { formatCurrencyCompact } from '@/utils/number-formatter.js';

const AVAILABLE_COLOR = '#2774E9';
const PENDING_COLOR = '#bfc9da';
const USED_COLOR = '#777777';

export default {
  name: 'FinancingLimitBar',
  components: {
    apexchart: VueApexCharts,
  },
  props: {
    title: {
      type: String,
      default: 'Credit Limit'
    },
    total: {
      type: [Number, String],
      default: 0
    },
    used: {
      type: [Number, String],
      default: 0
    },
    pending: {
      type: [Number, String],
      default: 0
    },
    available: {
      type: [Number, String],
      default: 0
    },
    currency: {
      type: String,
      default: 'USD'
    },
    usedLabel: {
      type: String,
      default: 'Used Limit'
    },
    pendingLabel: {
      type: String,
      default: 'Under Review'
    },
    availableLabel: {
      type: String,
      default: 'Available'
    },
    titleColor: {
      type: String,
      default: '#333333'
    },
    hasCardStyling: {
      type: Boolean,
      default: false
    },
    fontFamily: {
      type: String,
      default: 'Medium'
    },
    width: {
      type: [String, Number],
      default: '100%'
    },
    height: {
      type: [String, Number],
      default: null
    }
  },
  computed: {
    containerClass() {
      return this.hasCardStyling ? 'financing-limit-card' : 'financing-limit-content';
    },
    containerStyles() {
      const styles = {};
      const width = this.normalizeSize(this.width);
      if (width) {
        styles.width = width;
      }
      const height = this.normalizeSize(this.height);
      if (height) {
        styles.minHeight = height;
      }
      return styles;
    },
    titleFontFamily() {
      const fontMap = {
        'Light': 'Avenir-Light',
        'Medium': 'Avenir-Medium', 
        'Heavy': 'Avenir-Heavy'
      };
      return fontMap[this.fontFamily] || 'Avenir-Medium';
    },
    totalValue() {
      return Math.max(parseFloat(this.total) || 0, 0);
    },
    usedValue() {
      return Math.max(parseFloat(this.used) || 0, 0);
    },
    pendingValue() {
      return Math.max(parseFloat(this.pending) || 0, 0);
    },
    availableValue() {
      return Math.max(parseFloat(this.available) || 0, 0);
    },
    series() {
      const total = this.totalValue;
      if (total <= 0) {
        return [0, 0, 0];
      }

      const clamp = (value) => Math.min(Math.max(value, 0), total);

      return [
        clamp(this.availableValue),
        clamp(this.pendingValue),
        clamp(this.usedValue),
      ];
    },
    chartOptions() {
      return {
        labels: [this.availableLabel, this.pendingLabel, this.usedLabel],
        colors: [AVAILABLE_COLOR, PENDING_COLOR, USED_COLOR],
        chart: {
          animations: {
            enabled: true,
          },
        },
        dataLabels: {
          enabled: false,
        },
        tooltip: {
          enabled: false,
        },
        legend: {
          show: false,
        },
        plotOptions: {
          pie: {
            startAngle: 180,
            endAngle: -180,
            donut: {
              size: '75%',
              labels: {
                show: false,
              },
            },
            expandOnClick: false,
          },
        },
      };
    },
    formattedAvailable() {
      return formatCurrencyCompact(this.availableValue, this.currency);
    },
    formattedTotal() {
      return formatCurrencyCompact(this.totalValue, this.currency);
    },
    legendItems() {
      return [
        {
          key: 'available',
          label: this.availableLabel,
          color: AVAILABLE_COLOR,
          formattedValue: formatCurrencyCompact(this.availableValue, this.currency),
        },
        {
          key: 'pending',
          label: this.pendingLabel,
          color: PENDING_COLOR,
          formattedValue: formatCurrencyCompact(this.pendingValue, this.currency),
        },
        {
          key: 'used',
          label: this.usedLabel,
          color: USED_COLOR,
          formattedValue: formatCurrencyCompact(this.usedValue, this.currency),
        },
      ];
    },
    chartHeight() {
      const preferred = this.normalizeNumber(this.height);
      if (preferred) {
        // For smaller heights, reduce chart size more aggressively
        const calculatedHeight = preferred - 100; // Leave room for title and padding
        return Math.max(Math.min(calculatedHeight, 360), 180);
      }
      return 220; // Default size reduced from 260 to 220
    },
    chartWrapperStyles() {
      const size = this.chartHeight;
      return {
        width: `${size}px`,
        height: `${size}px`,
        maxWidth: '100%',
      };
    },
    isCompactMode() {
      const preferred = this.normalizeNumber(this.height);
      return preferred && preferred < 300;
    }
  },
  methods: {
    formatCurrencyCompact,
    normalizeSize(value) {
      if (value === null || value === undefined || value === '') return null;
      if (typeof value === 'number') return `${value}px`;
      if (typeof value === 'string') return value;
      return null;
    },
    normalizeNumber(value) {
      if (value === null || value === undefined || value === '') return null;
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        const match = value.match(/(\d+(?:\.\d+)?)/);
        return match ? parseFloat(match[1]) : null;
      }
      return null;
    },
  },
};
</script>

<style scoped lang="scss">
@import './FinancingLimitBar.scss';
</style> 